// =====================================================
// EVENT PARTICIPANTS SERVICE
// Stage 2: Participants & Pricing
// Manage event participants (ministers + guests) with pricing
// =====================================================

import { supabase } from '@/supabase.config';
import { calculatePrice, mapAuthorityPositionToRoleCode } from '@/Services/pricingEngine.service';
import { getPricingRulesByEvent } from '@/Services/pricingRules.service';
import type { ParticipantData, ParticipantType, PriceCalculationContext } from '@/types/pricing.types';
import { parseLocalDate } from '@/models/event';
import { recordPayment, reversePayment } from '@/Services/eventPayments.service';

// ===================
// TYPE DEFINITIONS
// ===================

export interface EventParticipant {
  id: string;
  event_id: string;
  minister_id: string | null;
  guest_id: string | null;
  participant_type: string;
  calculated_price: number | null;
  applied_pricing_rule_id: string | null;
  is_mandatory: boolean;
  /** Habilitado para votar. null = default (ministers yes, guests per election setting) */
  voting_enabled: boolean | null;
  /** PIN printed on the badge to vote from the phone */
  voting_pin: string | null;
  registration_date: string;
  payment_status: string | null;
  payment_amount: number | null;
  paid_at: string | null;
  paid_by: string | null;
  attendance_status: string | null;
  checked_in_at: string | null;
  checked_in_by: string | null;
  notes: string | null;
  registered_at: string | null;
  registered_by: string | null;
  updated_by: string | null;
  // Public pre-registration
  registration_status: 'PRE_REGISTERED' | 'VERIFIED' | 'REJECTED';
  registration_source: 'ADMIN' | 'PUBLIC' | 'IMPORT';
  public_token: string;
  registration_data: Record<string, unknown> | null;
  documents: Partial<Record<'ci_front' | 'ci_back' | 'voucher' | 'delegation_letter' | 'signature', string>>;
  /** Documents replaced or added by staff: [{ kind, old_key, new_key, by, at }] */
  document_replacements?: { kind: string; old_key: string | null; new_key: string; by: string | null; at: string }[];
  declaration_accepted_at: string | null;
  form_signed_at: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
}

export interface ParticipantWithDetails extends EventParticipant {
  minister?: {
    id: string;
    adb_code: string;
    dni?: string | null;
    full_name: string | null;
    degree_id: string | null;
    district_id: string | null;
    region_id: string | null;
    position: string | null;
    cell_phone?: string | null;
    district?: {
      id: string;
      name: string;
    } | null;
    region?: {
      id: string;
      name: string;
      district_id: string;
    } | null;
  } | null;
  guest?: {
    id: string;
    full_name: string;
    email: string | null;
    phone: string | null;
    dni?: string | null;
    is_delegate?: boolean;
    represented_name?: string | null;
    institution_note?: string | null;
    representing_minister_id: string | null;
    representing_minister?: {
      id: string;
      adb_code: string;
      full_name: string | null;
    } | null;
  } | null;
  pricing_rule?: {
    id: string;
    name: string;
    action_type: string;
    action_value: number;
  } | null;
  // Authority position if participant is an authority
  authority_position?: string;
  authority_entity?: string;
  authority_entity_type?: 'DISTRICT' | 'REGION';
}

/** Minister attributes that pricing rules look at (when the caller can't read the ministers table itself) */
export type MinisterPricingOverride = Pick<
  ParticipantData,
  'minister_degree' | 'minister_birth_date' | 'minister_district' | 'minister_region' | 'minister_role' | 'minister_roles'
>;

export interface CreateParticipantData {
  event_id: string;
  minister_id?: string;
  guest_id?: string;
  is_mandatory?: boolean;
  registration_date?: Date;
  calculate_price?: boolean; // If true, auto-calculate price using pricing rules
  /** Pricing data already known (registration desk): skips reading the minister record */
  minister_pricing_data?: MinisterPricingOverride;
  /** Snapshot of the registration form data (what the printable form shows) */
  registration_data?: Record<string, unknown>;
  /** Storage keys of documents collected at registration (CI photos, letter, signature…) */
  documents?: Record<string, string>;
  /** The person accepted the participation declaration (desk) */
  declaration_accepted?: boolean;
  /** A drawn signature was collected (desk) */
  form_signed?: boolean;
}

export interface ParticipantFilters {
  /** GUEST = non-delegate guests; DELEGATE = guests registered as delegates */
  type?: 'MINISTER' | 'DELEGATE' | 'GUEST' | 'ALL';
  search?: string;
  payment_status?: 'PENDING' | 'PARTIAL' | 'PAID' | 'REVERSED';
  registration_status?: 'PRE_REGISTERED' | 'VERIFIED' | 'REJECTED';
  attendance_status?: 'PENDING' | 'ATTENDED' | 'ABSENT';
  is_mandatory?: boolean;
  pricing_rule_id?: string;
  district_id?: string;
  region_id?: string;
}

function assertAcceptsRegistrations(status: string) {
  if (status !== 'OPEN') {
    throw new Error('El evento no está abierto a inscripciones');
  }
}

// ===================
// CRUD OPERATIONS
// ===================

/**
 * Get all participants for an event
 */
export async function getEventParticipants(
  eventId: string,
  filters?: ParticipantFilters
): Promise<ParticipantWithDetails[]> {
  // Territory filters run in the database: inner join on minister only when filtering
  const territoryFilter = !!(filters?.district_id || filters?.region_id);
  const ministerJoin = territoryFilter ? 'minister:minister_id!inner' : 'minister:minister_id';

  let query = supabase
    .from('event_participants')
    .select(
      `
      *,
      ${ministerJoin} (
        id,
        adb_code,
        dni,
        full_name,
        degree_id,
        district_id,
        region_id,
        position,
        cell_phone,
        district:district_id(id, name),
        region:region_id(id, name, district_id)
      ),
      guest:guest_id (
        id,
        full_name,
        email,
        phone,
        dni,
        is_delegate,
        represented_name,
        institution_note,
        representing_minister_id,
        representing_minister:representing_minister_id(
          id,
          adb_code,
          full_name
        )
      ),
      pricing_rule:applied_pricing_rule_id (
        id,
        name,
        action_type,
        action_value
      )
    `
    )
    .eq('event_id', eventId)
    .order('registration_date', { ascending: false});

  // Apply filters
  if (filters?.type === 'MINISTER') {
    query = query.not('minister_id', 'is', null);
  } else if (filters?.type === 'GUEST' || filters?.type === 'DELEGATE') {
    query = query.not('guest_id', 'is', null);
  }

  if (filters?.payment_status) {
    query = query.eq('payment_status', filters.payment_status);
  }

  if (filters?.registration_status) {
    query = query.eq('registration_status', filters.registration_status);
  }

  if (filters?.attendance_status) {
    query = query.eq('attendance_status', filters.attendance_status);
  }

  if (filters?.district_id) {
    query = query.eq('minister.district_id', filters.district_id);
  }

  if (filters?.region_id) {
    query = query.eq('minister.region_id', filters.region_id);
  }

  if (filters?.is_mandatory !== undefined) {
    query = query.eq('is_mandatory', filters.is_mandatory);
  }

  if (filters?.pricing_rule_id) {
    query = query.eq('applied_pricing_rule_id', filters.pricing_rule_id);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching event participants:', error);
    throw new Error(`Error al cargar participantes: ${error.message}`);
  }

  const participants = data || [];

  // Enrich with authority positions - cast to unknown first to avoid type issues
  const enrichedParticipants = await enrichParticipantsWithAuthority(participants as unknown as ParticipantWithDetails[]);

  // Apply client-side filters (span multiple tables or nested relations)
  let filteredParticipants = enrichedParticipants;

  if (filters?.type === 'DELEGATE') {
    filteredParticipants = filteredParticipants.filter((p) => p.guest?.is_delegate);
  } else if (filters?.type === 'GUEST') {
    filteredParticipants = filteredParticipants.filter((p) => !p.guest?.is_delegate);
  }

  if (filters?.search) {
    const searchLower = filters.search.toLowerCase();
    filteredParticipants = filteredParticipants.filter((p) => {
      const ministerName = p.minister?.full_name?.toLowerCase() || '';
      const ministerCode = p.minister?.adb_code?.toLowerCase() || '';
      const ministerDni = p.minister?.dni?.toLowerCase() || '';
      const guestName = p.guest?.full_name?.toLowerCase() || '';
      const guestEmail = p.guest?.email?.toLowerCase() || '';
      const guestDni = p.guest?.dni?.toLowerCase() || '';

      return (
        ministerName.includes(searchLower) ||
        ministerCode.includes(searchLower) ||
        ministerDni.includes(searchLower) ||
        guestName.includes(searchLower) ||
        guestEmail.includes(searchLower) ||
        guestDni.includes(searchLower)
      );
    });
  }

  return filteredParticipants as unknown as ParticipantWithDetails[];
}

/**
 * Get a single participant by ID
 */
export async function getParticipantById(id: string): Promise<ParticipantWithDetails | null> {
  const { data, error } = await supabase
    .from('event_participants')
    .select(
      `
      *,
      minister:minister_id (
        id,
        adb_code,
        full_name,
        degree_id,
        district_id,
        region_id,
        position
      ),
      guest:guest_id (
        id,
        full_name,
        email,
        phone
      ),
      pricing_rule:applied_pricing_rule_id (
        id,
        name,
        action_type,
        action_value
      )
    `
    )
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error('Error fetching participant:', error);
    throw new Error(`Error al cargar participante: ${error.message}`);
  }

  return data as unknown as ParticipantWithDetails;
}

/**
 * Check if a minister/guest is already registered for an event
 */
export async function isAlreadyRegistered(
  eventId: string,
  ministerId?: string,
  guestId?: string
): Promise<boolean> {
  let query = supabase.from('event_participants').select('id').eq('event_id', eventId);

  if (ministerId) {
    query = query.eq('minister_id', ministerId);
  } else if (guestId) {
    query = query.eq('guest_id', guestId);
  } else {
    return false; // Need either minister or guest ID
  }

  const { data, error } = await query.limit(1);

  if (error) {
    console.error('Error checking registration:', error);
    return false;
  }

  return (data?.length || 0) > 0;
}

/**
 * Register a participant for an event
 */
export async function registerParticipant(
  data: CreateParticipantData
): Promise<ParticipantWithDetails> {
  // Validate: must have either minister_id or guest_id
  if (!data.minister_id && !data.guest_id) {
    throw new Error('Debe proporcionar un ministro o invitado');
  }

  // Check for duplicate registration
  const alreadyRegistered = await isAlreadyRegistered(
    data.event_id,
    data.minister_id,
    data.guest_id
  );

  if (alreadyRegistered) {
    throw new Error('Este participante ya está registrado en el evento');
  }

  // Get event to check status + base price
  const { data: event, error: eventError } = await supabase
    .from('events')
    .select('id, base_price, start_date, status')
    .eq('id', data.event_id)
    .single();

  if (eventError || !event) {
    throw new Error('Evento no encontrado');
  }

  assertAcceptsRegistrations(event.status);

  let calculatedPrice: number | null = null;
  let appliedRuleId: string | null = null;

  // Calculate price if enabled and event has base price
  if (data.calculate_price !== false && event.base_price) {
    try {
      const priceResult = await calculateParticipantPrice(
        data.event_id,
        event.base_price,
        data.minister_id,
        data.guest_id,
        data.registration_date || new Date(),
        parseLocalDate(event.start_date) ?? new Date(event.start_date),
        { ministerData: data.minister_pricing_data }
      );

      calculatedPrice = priceResult.final_price;
      appliedRuleId = priceResult.applied_rule?.id || null;
    } catch (error) {
      console.error('Error calculating price:', error);
      // Continue with base price if calculation fails
      calculatedPrice = event.base_price;
    }
  }

  // Insert participant
  const { data: participant, error } = await supabase
    .from('event_participants')
    .insert({
      event_id: data.event_id,
      minister_id: data.minister_id || null,
      guest_id: data.guest_id || null,
      participant_type: data.minister_id ? 'MINISTER' : 'GUEST',
      calculated_price: calculatedPrice ?? event.base_price ?? 0,
      applied_pricing_rule_id: appliedRuleId,
      is_mandatory: data.is_mandatory || false,
      registration_date: (data.registration_date || new Date()).toISOString(),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      registration_data: (data.registration_data ?? null) as any,
      ...(data.documents ? { documents: data.documents } : {}),
      declaration_accepted_at: data.declaration_accepted ? new Date().toISOString() : null,
      form_signed_at: data.form_signed ? new Date().toISOString() : null,
    })
    .select()
    .single();

  if (error) {
    console.error('Error registering participant:', error);
    throw new Error(`Error al registrar participante: ${error.message}`);
  }

  // Fetch full details
  return (await getParticipantById(participant.id))!;
}

/**
 * "Habilitado para votar" for several participants at once. Returns how many rows changed.
 */
export async function setVotingEnabled(participantIds: string[], enabled: boolean): Promise<number> {
  if (participantIds.length === 0) return 0;
  const { data, error } = await supabase.rpc('set_voting_enabled', {
    p_participant_ids: participantIds,
    p_enabled: enabled,
  });
  if (error) {
    console.error('Error setting voting_enabled:', error);
    throw new Error(`No se pudo cambiar la habilitación para votar: ${error.message}`);
  }
  return data ?? 0;
}

/**
 * Unregister a participant from an event
 */
export async function unregisterParticipant(id: string): Promise<void> {
  const { data: participant, error: fetchError } = await supabase
    .from('event_participants')
    .select('payment_status')
    .eq('id', id)
    .single();

  if (fetchError) {
    throw new Error(`Error al obtener participante: ${fetchError.message}`);
  }

  if (participant?.payment_status === 'PAID' || participant?.payment_status === 'PARTIAL') {
    throw new Error('No se puede eliminar un participante con pagos registrados. Revierta el pago primero.');
  }

  const { error } = await supabase.from('event_participants').delete().eq('id', id);

  if (error) {
    console.error('Error unregistering participant:', error);
    throw new Error(`Error al desregistrar participante: ${error.message}`);
  }
}

/**
 * Update participant data
 */
export async function updateParticipant(
  id: string,
  data: Partial<CreateParticipantData>
): Promise<ParticipantWithDetails> {
  const updateData: Record<string, unknown> = {};

  if (data.is_mandatory !== undefined) {
    updateData.is_mandatory = data.is_mandatory;
  }

  const { data: participant, error } = await supabase
    .from('event_participants')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .update(updateData as any)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating participant:', error);
    throw new Error(`Error al actualizar participante: ${error.message}`);
  }

  return (await getParticipantById(participant.id))!;
}

/**
 * Update participant payment status — routed through the payments ledger so
 * history is kept (PAID → records the outstanding balance, PENDING → reverses).
 */
export async function updateParticipantPaymentStatus(
  id: string,
  paymentStatus: 'PAID' | 'PENDING',
  paymentAmount?: number
): Promise<void> {
  if (paymentStatus === 'PAID') {
    const { data: p, error } = await supabase
      .from('event_participants')
      .select('calculated_price, payment_amount')
      .eq('id', id)
      .single();
    if (error || !p) throw new Error('Participante no encontrado');
    const amount = paymentAmount ?? Math.max(0, Number(p.calculated_price ?? 0) - Number(p.payment_amount ?? 0));
    if (amount > 0) await recordPayment({ participant_id: id, payment_amount: amount });
  } else {
    await reversePayment({ participant_id: id, reversal_reason: 'Estado de pago restablecido a pendiente' });
  }
}

/**
 * Bulk update payment status for multiple participants
 */
export async function bulkUpdatePaymentStatus(
  participantIds: string[],
  paymentStatus: 'PAID' | 'PENDING'
): Promise<{ updated: number; errors: number }> {
  let updated = 0;
  let errors = 0;

  for (const id of participantIds) {
    try {
      await updateParticipantPaymentStatus(id, paymentStatus);
      updated++;
    } catch (error) {
      console.error(`Error updating payment for ${id}:`, error);
      errors++;
    }
  }

  return { updated, errors };
}

// ===================
// PRICE CALCULATION
// ===================

/**
 * Calculate price for a participant using pricing rules
 */
export async function calculateParticipantPrice(
  eventId: string,
  basePrice: number,
  ministerId?: string,
  guestId?: string,
  registrationDate: Date = new Date(),
  eventStartDate?: Date,
  options: { participantType?: ParticipantType; ministerData?: MinisterPricingOverride } = {}
) {
  // Get pricing rules for event
  const rules = await getPricingRulesByEvent(eventId, { is_active: true });

  // Date-based rules need the real start date (callers without it get it from the event)
  if (!eventStartDate) {
    const { data: event } = await supabase.from('events').select('start_date').eq('id', eventId).single();
    eventStartDate = (event && (parseLocalDate(event.start_date) ?? new Date(event.start_date))) || new Date();
  }

  // Build participant data
  const participantData: ParticipantData = {
    registration_date: registrationDate,
    event_start_date: eventStartDate,
    participant_type: options.participantType,
  };

  // Fetch minister data if applicable
  if (ministerId && options.ministerData) {
    participantData.minister_id = ministerId;
    participantData.participant_type ??= 'MINISTER';
    Object.assign(participantData, options.ministerData);
  } else if (ministerId) {
    const { data: minister, error } = await supabase
      .from('ministers')
      .select('id, degree_id, district_id, region_id, position, birth_date')
      .eq('id', ministerId)
      .single();

    if (!error && minister) {
      participantData.minister_id = minister.id;
      participantData.participant_type ??= 'MINISTER';
      participantData.minister_degree = minister.degree_id || undefined;
      participantData.minister_birth_date = minister.birth_date ? parseLocalDate(minister.birth_date) : undefined;
      participantData.minister_district = minister.district_id || undefined;
      participantData.minister_region = minister.region_id || undefined;
      participantData.minister_role = minister.position || undefined;
      participantData.minister_roles = await getActiveAuthorityRoleCodes(minister.id);
    }
  }

  // Fetch guest data if applicable
  if (guestId) {
    const { data: guest, error } = await supabase
      .from('event_guests')
      .select('id, is_delegate')
      .eq('id', guestId)
      .single();

    if (!error && guest) {
      participantData.guest_id = guest.id;
      participantData.participant_type ??= guest.is_delegate ? 'DELEGATE' : 'GUEST';
    }
  }

  // Registrations ahead of this one (rejected ones don't take a place)
  const { count } = await supabase
    .from('event_participants')
    .select('*', { count: 'exact', head: true })
    .eq('event_id', eventId)
    .neq('registration_status', 'REJECTED');

  // Calculate price
  const context: PriceCalculationContext = {
    event_id: eventId,
    base_price: basePrice,
    participant_data: participantData,
    current_participant_count: count || 0,
  };

  return calculatePrice(context, rules);
}

/** Role codes for the minister's currently active authority positions */
async function getActiveAuthorityRoleCodes(ministerId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('authority_positions')
    .select('org_type, role')
    .eq('minister_id', ministerId)
    .is('ended_at', null);
  if (error) {
    console.error('Error fetching authority positions:', error);
    return [];
  }
  return (data ?? [])
    .map((p) => mapAuthorityPositionToRoleCode(p.org_type, p.role))
    .filter((c): c is string => !!c);
}

type MinisterPricingData = Pick<
  ParticipantData,
  'minister_id' | 'minister_degree' | 'minister_birth_date' | 'minister_district' | 'minister_region' | 'minister_role' | 'minister_roles'
>;

/** Pricing attributes for many ministers in 2 queries (instead of 2 per minister) */
async function loadMinisterPricingData(ministerIds: string[]): Promise<Map<string, MinisterPricingData>> {
  const result = new Map<string, MinisterPricingData>();
  const ids = [...new Set(ministerIds)];
  for (let i = 0; i < ids.length; i += 200) {
    const chunk = ids.slice(i, i + 200);
    const [ministersRes, positionsRes] = await Promise.all([
      supabase.from('ministers').select('id, degree_id, district_id, region_id, position, birth_date').in('id', chunk),
      supabase.from('authority_positions').select('minister_id, org_type, role').in('minister_id', chunk).is('ended_at', null),
    ]);
    if (ministersRes.error) throw new Error(`Error al cargar ministros: ${ministersRes.error.message}`);

    const rolesByMinister = new Map<string, string[]>();
    for (const p of positionsRes.data ?? []) {
      const code = mapAuthorityPositionToRoleCode(p.org_type, p.role);
      if (code) rolesByMinister.set(p.minister_id, [...(rolesByMinister.get(p.minister_id) ?? []), code]);
    }
    for (const m of ministersRes.data ?? []) {
      result.set(m.id, {
        minister_id: m.id,
        minister_degree: m.degree_id || undefined,
        minister_birth_date: m.birth_date ? parseLocalDate(m.birth_date) : undefined,
        minister_district: m.district_id || undefined,
        minister_region: m.region_id || undefined,
        minister_role: m.position || undefined,
        minister_roles: rolesByMinister.get(m.id) ?? [],
      });
    }
  }
  return result;
}

/**
 * Recalculate prices for an event's participants (all, or the given ids).
 * - Paid participants keep the price they paid.
 * - PARTICIPANT_COUNT uses how many registered *before* each participant, so
 *   recalculating doesn't drift prices as the event grows.
 * - Rules, event and minister data load once; only changed rows are written.
 */
export async function recalculateParticipantPrices(
  eventId: string,
  participantIds?: string[],
): Promise<{ updated: number; skippedPaid: number }> {
  const [{ data: event, error: eventError }, rules, { data: rows, error: rowsError }] = await Promise.all([
    supabase.from('events').select('id, base_price, start_date').eq('id', eventId).single(),
    getPricingRulesByEvent(eventId, { is_active: true }),
    supabase
      .from('event_participants')
      .select('id, minister_id, guest_id, registration_date, registration_status, payment_status, calculated_price, applied_pricing_rule_id')
      .eq('event_id', eventId)
      .order('registration_date', { ascending: true }),
  ]);

  if (eventError || !event) throw new Error('Evento no encontrado');
  if (rowsError) throw new Error(`Error al cargar participantes: ${rowsError.message}`);

  const all = rows ?? [];
  const wanted = participantIds ? new Set(participantIds) : null;
  const targets = all.filter((p) => !wanted || wanted.has(p.id));
  const skippedPaid = targets.filter((p) => p.payment_status === 'PAID').length;
  const toPrice = targets.filter((p) => p.payment_status !== 'PAID');

  const ministers = await loadMinisterPricingData(
    toPrice.map((p) => p.minister_id).filter((id): id is string => !!id),
  );
  const eventStart = parseLocalDate(event.start_date) ?? new Date(event.start_date);

  // Registrations ahead of each participant, in registration order (rejected ones don't take a place)
  const orderIndex = new Map<string, number>();
  let ahead = 0;
  for (const p of all) {
    orderIndex.set(p.id, ahead);
    if (p.registration_status !== 'REJECTED') ahead++;
  }

  const guestIds = toPrice.map((p) => p.guest_id).filter((id): id is string => !!id);
  const delegateIds = new Set<string>();
  for (let i = 0; i < guestIds.length; i += 200) {
    const { data: guests, error: guestsError } = await supabase
      .from('event_guests')
      .select('id, is_delegate')
      .in('id', guestIds.slice(i, i + 200));
    if (guestsError) throw new Error(`Error al cargar invitados: ${guestsError.message}`);
    (guests ?? []).filter((g) => g.is_delegate).forEach((g) => delegateIds.add(g.id));
  }

  const updates = toPrice
    .map((p) => {
      const result = calculatePrice(
        {
          event_id: eventId,
          base_price: event.base_price ?? 0,
          participant_data: {
            ...(p.minister_id ? ministers.get(p.minister_id) : {}),
            guest_id: p.guest_id ?? undefined,
            participant_type: p.minister_id ? 'MINISTER' : delegateIds.has(p.guest_id ?? '') ? 'DELEGATE' : 'GUEST',
            registration_date: new Date(p.registration_date),
            event_start_date: eventStart,
          },
          current_participant_count: orderIndex.get(p.id) ?? 0,
        },
        rules,
      );
      return { id: p.id, price: result.final_price, ruleId: result.applied_rule?.id ?? null, prev: p };
    })
    .filter((u) => u.price !== Number(u.prev.calculated_price) || u.ruleId !== u.prev.applied_pricing_rule_id);

  // Bounded concurrency — avoid hundreds of simultaneous requests
  for (let i = 0; i < updates.length; i += 10) {
    const results = await Promise.all(
      updates.slice(i, i + 10).map((u) =>
        supabase
          .from('event_participants')
          .update({ calculated_price: u.price, applied_pricing_rule_id: u.ruleId })
          .eq('id', u.id),
      ),
    );
    const failed = results.find((r) => r.error);
    if (failed?.error) throw new Error(`Error al actualizar precios: ${failed.error.message}`);
  }

  return { updated: updates.length, skippedPaid };
}

/**
 * Recalculate price for an existing participant
 */
export async function recalculateParticipantPrice(participantId: string): Promise<void> {
  const { data: participant, error } = await supabase
    .from('event_participants')
    .select('event_id, payment_status')
    .eq('id', participantId)
    .single();
  if (error || !participant) {
    throw new Error('Participante no encontrado');
  }
  if (participant.payment_status === 'PAID') {
    throw new Error('El participante ya pagó; revierta el pago para recalcular el precio');
  }
  await recalculateParticipantPrices(participant.event_id, [participantId]);
}

/**
 * Recalculate all participants' prices for an event (paid ones are skipped)
 */
export async function recalculateAllParticipantPrices(eventId: string): Promise<void> {
  await recalculateParticipantPrices(eventId);
}

// ===================
// STATISTICS
// ===================

/**
 * Get participant statistics for an event
 */
export async function getEventParticipantStatistics(eventId: string) {
  const participants = await getEventParticipants(eventId);

  const stats = {
    total_participants: participants.length,
    total_ministers: participants.filter((p) => p.minister_id).length,
    total_guests: participants.filter((p) => p.guest_id).length,
    mandatory_participants: participants.filter((p) => p.is_mandatory).length,
    // Revenue = actual payment_amount for PAID, calculated_price for all others
    total_revenue: participants.reduce((sum, p) => {
      const amount = p.payment_status === 'PAID'
        ? (p.payment_amount || 0)
        : (p.calculated_price || 0);
      return sum + amount;
    }, 0),
    average_price: 0,
    by_pricing_rule: new Map<
      string,
      { rule_name: string; count: number; total_discount: number }
    >(),
  };

  if (stats.total_participants > 0) {
    stats.average_price = stats.total_revenue / stats.total_participants;
  }

  // Aggregate by pricing rule
  participants.forEach((p) => {
    if (p.pricing_rule) {
      const ruleId = p.pricing_rule.id;
      if (!stats.by_pricing_rule.has(ruleId)) {
        stats.by_pricing_rule.set(ruleId, {
          rule_name: p.pricing_rule.name,
          count: 0,
          total_discount: 0,
        });
      }
      const ruleStat = stats.by_pricing_rule.get(ruleId)!;
      ruleStat.count++;
    }
  });

  return {
    ...stats,
    by_pricing_rule: Array.from(stats.by_pricing_rule.values()),
  };
}

// ===================
// BULK OPERATIONS
// ===================

export interface AuthorityMinister {
  minister_id: string;
  position: string;
  entity_type: 'DISTRICT' | 'REGION' | 'NATIONAL';
  entity_id: string | null;
  entity_name: string;
}

const POSITION_LABELS: Record<string, string> = {
  SUPERINTENDENT: 'Superintendente',
  VICE_SUPERINTENDENT: 'Vice Superintendente',
  PRESBYTER: 'Presbítero',
  VICE_PRESBYTER: 'Vice Presbítero',
  PRESIDENT: 'Presidente',
  VICE_PRESIDENT: 'Vice Presidente',
  SECRETARY: 'Secretario',
  TREASURER: 'Tesorero',
  VOCAL_1: 'Vocal 1',
  VOCAL_2: 'Vocal 2',
};

// National > district > region when a minister holds several positions
const ENTITY_RANK: Record<string, number> = { NATIONAL: 0, DISTRICT: 1, REGION: 2 };

/**
 * Active authority positions (authority_positions with ended_at NULL), one per
 * minister (highest-ranked entity wins). Source of truth since the tithes
 * module — the legacy districts/regions *_id columns are no longer read.
 */
async function fetchActiveAuthorities(
  scope: 'ALL' | 'DISTRICT' | 'REGION',
  ministerIds?: string[],
): Promise<AuthorityMinister[]> {
  let query = supabase
    .from('authority_positions')
    .select('minister_id, org_type, role, district:district_id(id, name), region:region_id(id, name)')
    .is('ended_at', null);

  if (scope !== 'ALL') query = query.eq('org_type', scope);
  if (ministerIds) query = query.in('minister_id', ministerIds);

  const { data, error } = await query;
  if (error) {
    throw new Error(`Error al obtener autoridades: ${error.message}`);
  }

  type Row = {
    minister_id: string;
    org_type: 'DISTRICT' | 'REGION' | 'NATIONAL';
    role: string;
    district: { id: string; name: string } | null;
    region: { id: string; name: string } | null;
  };

  const byMinister = new Map<string, AuthorityMinister>();
  for (const row of (data ?? []) as unknown as Row[]) {
    const entity = row.org_type === 'DISTRICT' ? row.district : row.org_type === 'REGION' ? row.region : null;
    const candidate: AuthorityMinister = {
      minister_id: row.minister_id,
      position: POSITION_LABELS[row.role] ?? row.role,
      entity_type: row.org_type,
      entity_id: entity?.id ?? null,
      entity_name: row.org_type === 'NATIONAL' ? 'Nacional' : entity?.name ?? 'Sin nombre',
    };
    const current = byMinister.get(row.minister_id);
    if (!current || ENTITY_RANK[candidate.entity_type] < ENTITY_RANK[current.entity_type]) {
      byMinister.set(row.minister_id, candidate);
    }
  }
  return [...byMinister.values()];
}

/**
 * Enrich participants with authority position information
 */
async function enrichParticipantsWithAuthority(
  participants: ParticipantWithDetails[]
): Promise<ParticipantWithDetails[]> {
  const ministerIds = participants.map((p) => p.minister_id).filter((id): id is string => !!id);
  if (ministerIds.length === 0) {
    return participants;
  }

  let authorities: AuthorityMinister[] = [];
  try {
    authorities = await fetchActiveAuthorities('ALL', ministerIds);
  } catch (error) {
    console.error('Error fetching authority data:', error);
    return participants; // Return without enrichment if error
  }

  const authorityMap = new Map(authorities.map((a) => [a.minister_id, a]));
  return participants.map((p) => {
    const authority = p.minister_id ? authorityMap.get(p.minister_id) : undefined;
    if (!authority) return p;
    return {
      ...p,
      authority_position: authority.position,
      authority_entity: authority.entity_name,
      authority_entity_type: authority.entity_type === 'NATIONAL' ? undefined : authority.entity_type,
    };
  });
}

/**
 * Get all active authorities (district / region / national positions)
 */
export async function getAllActiveAuthorities(
  scope: 'ALL' | 'DISTRICT' | 'REGION' = 'ALL'
): Promise<AuthorityMinister[]> {
  return fetchActiveAuthorities(scope);
}

/**
 * Bulk register authorities as event participants (OPTIMIZED with batch insert)
 */
export async function bulkRegisterAuthorities(
  eventId: string,
  scope: 'ALL' | 'DISTRICT' | 'REGION' = 'ALL',
  onProgress?: (current: number, total: number) => void
): Promise<{ registered: number; skipped: number; errors: number }> {
  // Get event info for price calculation
  const { data: event, error: eventError } = await supabase
    .from('events')
    .select('id, base_price, start_date, status')
    .eq('id', eventId)
    .single();

  if (eventError || !event) {
    throw new Error('Evento no encontrado');
  }

  assertAcceptsRegistrations(event.status);

  // Get all authorities and existing participants in parallel
  const [authorities, existingParticipants] = await Promise.all([
    getAllActiveAuthorities(scope),
    getEventParticipants(eventId),
  ]);

  const total = authorities.length;

  // Build set of existing minister IDs for fast lookup
  const existingMinisterIds = new Set(
    existingParticipants
      .filter((p) => p.minister_id)
      .map((p) => p.minister_id as string)
  );

  // Filter out already registered ministers
  const authoritiesToRegister = authorities.filter(
    (auth) => !existingMinisterIds.has(auth.minister_id)
  );

  const skipped = authorities.length - authoritiesToRegister.length;

  if (authoritiesToRegister.length === 0) {
    if (onProgress) onProgress(total, total);
    return { registered: 0, skipped, errors: 0 };
  }

  // Prepare batch insert data
  const insertData = authoritiesToRegister.map((authority) => ({
    event_id: eventId,
    minister_id: authority.minister_id,
    participant_type: 'MINISTER' as const,
    calculated_price: event.base_price ?? 0,
    applied_pricing_rule_id: null,
    is_mandatory: true,
    registration_date: new Date().toISOString(),
  }));

  // Report progress
  if (onProgress) {
    onProgress(authoritiesToRegister.length, total);
  }

  // BATCH INSERT - Single query instead of N queries!
  const { data: insertedParticipants, error: insertError } = await supabase
    .from('event_participants')
    .insert(insertData)
    .select('id, minister_id');

  if (insertError) {
    console.error('Error batch inserting authorities:', insertError);
    throw new Error(`Error al registrar autoridades: ${insertError.message}`);
  }

  const registered = insertedParticipants?.length || 0;

  // Apply pricing rules to the new rows (one batch, not per participant)
  if (registered > 0) {
    await recalculateParticipantPrices(eventId, (insertedParticipants ?? []).map((p) => p.id));
  }

  return { registered, skipped, errors: 0 };
}

// ===================
// MINISTER PROFILE: EVENTS
// ===================

export interface MinisterEventRegistration {
  participantId: string;
  publicToken: string;
  event: { id: string; name: string; code: string; location: string | null; start_date: string };
  /** MINISTER: registered himself/herself. DELEGATE: someone attended on behalf of the minister. */
  role: 'MINISTER' | 'DELEGATE';
  /** Who attended (delegates only) */
  delegateName: string | null;
  registrationStatus: 'PRE_REGISTERED' | 'VERIFIED' | 'REJECTED';
  paymentStatus: string | null;
  attendanceStatus: string | null;
}

const MINISTER_EVENT_SELECT = `
  id, public_token, registration_status, payment_status, attendance_status,
  event:event_id ( id, name, code, location, start_date )
`;

/**
 * Events a minister took part in: their own registrations plus those where a
 * delegate attended on their behalf. Newest event first.
 */
export async function getMinisterEventRegistrations(ministerId: string): Promise<MinisterEventRegistration[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const db = supabase as any;
  const [own, delegated] = await Promise.all([
    db.from('event_participants').select(MINISTER_EVENT_SELECT).eq('minister_id', ministerId),
    db
      .from('event_participants')
      .select(`${MINISTER_EVENT_SELECT}, guest:guest_id!inner ( full_name, representing_minister_id )`)
      .eq('guest.representing_minister_id', ministerId),
  ]);
  if (own.error) throw new Error(`Error al cargar eventos: ${own.error.message}`);
  if (delegated.error) throw new Error(`Error al cargar eventos: ${delegated.error.message}`);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const toRow = (r: any, role: 'MINISTER' | 'DELEGATE'): MinisterEventRegistration => ({
    participantId: r.id,
    publicToken: r.public_token,
    event: r.event,
    role,
    delegateName: role === 'DELEGATE' ? (r.guest?.full_name ?? null) : null,
    registrationStatus: r.registration_status,
    paymentStatus: r.payment_status,
    attendanceStatus: r.attendance_status,
  });

  return [
    ...(own.data ?? []).map((r: unknown) => toRow(r, 'MINISTER')),
    ...(delegated.data ?? []).map((r: unknown) => toRow(r, 'DELEGATE')),
  ]
    .filter((r) => r.event && r.publicToken)
    .sort((a, b) => b.event.start_date.localeCompare(a.event.start_date));
}
